import { BUILD_CAPABILITY, buildErrors, isBuildImage, type BuildInput } from './imageBuild'
import { ALLOWLIST_CAPABILITY, normalizeAllowFrom } from './allowFrom'
import { SIZE_AXES, isSizePresetId, matchesPreset, type SizeAxis, type SizePresetId } from './sizing'
import { normalizeS3ConnectionRecord, type S3ConnectionRecord } from './s3Connection'

export interface GuidedEnvironment {
  key: string
  value: string
}

/**
 * One port the workload listens on. It is always exposed globally, under its
 * own number: the host assigns the public port it is reachable on (reported in
 * the lease status), so a tenant-chosen public number or a private toggle
 * would be a form field with no effect.
 */
export interface GuidedPort {
  container: number
  protocol: 'tcp' | 'udp'
  /**
   * Access: absent means Anyone (the default, and every draft saved before
   * this existed). Present means "Only these IPs", with canonical entries
   * (core/allowFrom.ts); an empty list is a draft mid-edit and cannot be
   * rented — guidedDraftToSdl refuses it rather than emit an open port.
   */
  allowFrom?: string[]
  /**
   * The largest HTTP request body, in MiB, the host's ingress accepts on this
   * port. Only a TCP port 80 is served through that ingress (it is exposed as
   * 80, and the provider turns an external port 80 into an Ingress), so it is
   * emitted as `http_options.max_body_size` there and nowhere else. Absent: the
   * SDL default, 1 MiB.
   */
  maxBodyMiB?: number | undefined
}

/**
 * A persistent volume: a named storage entry with `persistent: true`, mounted
 * into the container. It survives restarts and in-place updates of the
 * instance and is deleted with it — closing a lease deletes its namespace,
 * volumes included. `sizeGi` is in the same GB the Storage slider shows.
 */
export interface GuidedVolume {
  mount: string
  sizeGi: number
}

/** The SDL storage name the guided form gives its one volume. */
export const GUIDED_VOLUME_NAME = 'data'

/** Bounds for a guided volume, in GB. The upper bound matches the Storage slider's default maximum. */
export const GUIDED_VOLUME_BOUNDS = { min: 1, max: 500 } as const

/** The port whose requests the host's HTTPS ingress serves (see `GuidedPort.maxBodyMiB`). */
export const INGRESS_PORT = 80

const MIB = 1024 * 1024

/**
 * The guided body-size ceiling: `pkg.akt.dev/go/sdl` v0.1.1 refuses a
 * `max_body_size` above 104857600 bytes (`upperLimitBodySize`), and the
 * provider applies what it is given without a lower cap of its own.
 */
export const GUIDED_MAX_BODY_MIB = HTTP_OPTION_LIMITS.maxBodySize / MIB

export interface GuidedDraft {
  workloadSource?: 'image' | 'dockerfile' | undefined
  buildInput?: BuildInput | undefined
  name: string
  image: string
  cpu: number
  ram: number
  storage: number
  autoRecovery: boolean
  recoveryMode: 'single' | 'standby' | 'replicated'
  replicaCount: number
  priceCap: string
  /**
   * Provider-derived hourly price for the direct custom-rental path.
   *
   * This is deliberately separate from `priceCap`: an empty cap is the
   * persisted marker for a provider-priced rental, while this value is the
   * exact provider reference price used to build the SDL ceiling. Keeping the
   * two apart means a provider price can never be mistaken for a tenant cap
   * and silently switch the rental back to competitive mode.
   */
  providerPostedPrice?: string | undefined
  ports: GuidedPort[]
  env: GuidedEnvironment[]
  /**
   * The unencrypted exposure the tenant accepted (`PlaintextExposure.key` in
   * popularImages.ts). Not part of the SDL; a different image or port set no
   * longer matches it, so the tenant is asked again.
   */
  acknowledgedPlaintext?: string | undefined
  /**
   * The resources the tenant has set on the custom-capacity path. That path
   * starts with nothing chosen rather than a size the console picked — on a
   * small host the default would clamp to everything free, and renting the
   * whole of it should be the tenant's decision. Only the custom path reads it.
   */
  sizedAxes?: SizeAxis[] | undefined
  /**
   * The Size card's choice on a host (core/sizing `SizePresetId`). `whole`
   * rents the host product; every other choice is custom capacity. Not part
   * of the SDL. Drafts saved before it existed have none, and Configure
   * derives one from their values (`derivePreset`).
   */
  sizePreset?: SizePresetId | undefined
  /**
   * The size is the one Configure opened on (the suggested size, a smaller
   * preset, or the whole host), not one the tenant picked. Cleared by any
   * size the tenant sets. Not part of the SDL.
   */
  sizeAutoChosen?: boolean | undefined
  /** An optional persistent volume. Absent: the only storage is the ephemeral Storage size. */
  volume?: GuidedVolume | undefined
  /**
   * The S3 connection the environment holds: which variables it wrote, so an
   * edit or a removal touches exactly those. Not part of the SDL; the values
   * are the environment rows themselves.
   */
  s3Connection?: S3ConnectionRecord | undefined
}

export const DEFAULT_GUIDED_DRAFT: GuidedDraft = {
  name: 'my-instance',
  image: 'nginx:1.27-alpine',
  cpu: 1000,
  ram: 2,
  storage: 10,
  autoRecovery: false,
  recoveryMode: 'single',
  replicaCount: 1,
  priceCap: '0.025',
  ports: [{ container: 80, protocol: 'tcp' }],
  env: [],
}

/** Minimum first deployment escrow in the chain's micro denomination. */
export const DEFAULT_DEPLOYMENT_DEPOSIT_MICRO = '500000'

/**
 * The highest per-hour ceiling the chain can accept for an open request.
 *
 * The deployment protocol still requires a positive placement amount, even
 * when the tenant has chosen not to set a maximum. At six-second blocks,
 * 6,000 DDT/hour converts to the chain's 10,000,000 micro-DDT/block limit.
 */
export const OPEN_REQUEST_HOURLY_CEILING = '6000'

function normalizeResource(value: unknown, fallback: number, minimum: number): number {
  const numeric = typeof value === 'number' && Number.isFinite(value) ? value : fallback
  return Math.max(minimum, Math.round(numeric))
}

export function normalizeGuidedDraft(parsed: Partial<GuidedDraft>): GuidedDraft {
  const recoveryMode = parsed.recoveryMode ?? (parsed.autoRecovery ? 'standby' : DEFAULT_GUIDED_DRAFT.recoveryMode)
  const rawProviderPostedPrice = typeof parsed.providerPostedPrice === 'string' ? parsed.providerPostedPrice.trim() : ''
  const providerPostedPrice = /^\d+(?:\.\d+)?$/.test(rawProviderPostedPrice) && !/^0+(?:\.0+)?$/.test(rawProviderPostedPrice)
    ? rawProviderPostedPrice
    : undefined
  return {
    ...DEFAULT_GUIDED_DRAFT,
    ...parsed,
    // Keep persisted drafts inside the guided form's legal range. In
    // particular, a zero-capacity host must never leave 0/0/0 behind for a
    // later competitive or path-less request, whose sliders are unbounded.
    cpu: normalizeResource(parsed.cpu, DEFAULT_GUIDED_DRAFT.cpu, 250),
    ram: normalizeResource(parsed.ram, DEFAULT_GUIDED_DRAFT.ram, 1),
    storage: normalizeResource(parsed.storage, DEFAULT_GUIDED_DRAFT.storage, 1),
    providerPostedPrice,
    recoveryMode,
    autoRecovery: recoveryMode !== 'single',
    replicaCount: Number.isInteger(parsed.replicaCount) && (parsed.replicaCount ?? 0) >= 2 ? Math.min(10, parsed.replicaCount as number) : DEFAULT_GUIDED_DRAFT.replicaCount,
    ports: Array.isArray(parsed.ports) ? parsed.ports.map(normalizePort) : DEFAULT_GUIDED_DRAFT.ports,
    env: Array.isArray(parsed.env) ? parsed.env : DEFAULT_GUIDED_DRAFT.env,
    acknowledgedPlaintext: typeof parsed.acknowledgedPlaintext === 'string' ? parsed.acknowledgedPlaintext : undefined,
    sizedAxes: Array.isArray(parsed.sizedAxes) ? SIZE_AXES.filter((axis) => parsed.sizedAxes?.includes(axis)) : undefined,
    sizePreset: isSizePresetId(parsed.sizePreset) ? parsed.sizePreset : undefined,
    sizeAutoChosen: parsed.sizeAutoChosen === true ? true : undefined,
    // Drafts saved before volumes existed have no such field and keep none.
    volume: normalizeVolume(parsed.volume),
    s3Connection: normalizeS3ConnectionRecord(parsed.s3Connection),
  }
}

function normalizeVolume(volume: unknown): GuidedVolume | undefined {
  if (typeof volume !== 'object' || volume === null) return undefined
  const { mount, sizeGi } = volume as Partial<GuidedVolume>
  if (typeof mount !== 'string' || typeof sizeGi !== 'number' || !Number.isFinite(sizeGi)) return undefined
  return { mount, sizeGi: Math.round(sizeGi) }
}

/**
 * Why a volume cannot be deployed, per field, or nothing. The mount is an
 * absolute container path below the root: the provider mounts it over whatever
 * the image has there, so `/` itself, a relative path, `..` or whitespace is
 * refused before signing rather than as a pod that never starts.
 */
export function volumeErrors(volume: GuidedVolume): { mount?: string; size?: string } {
  const out: { mount?: string; size?: string } = {}
  const mount = volume.mount.trim()
  if (!/^\/[A-Za-z0-9._\-/]+$/.test(mount) || mount === '/' || mount.split('/').some((part) => part === '..' || part === '.')) {
    out.mount = 'Enter an absolute path in the container, such as /data.'
  }
  if (!Number.isInteger(volume.sizeGi) || volume.sizeGi < GUIDED_VOLUME_BOUNDS.min || volume.sizeGi > GUIDED_VOLUME_BOUNDS.max) {
    out.size = `Enter a size from ${GUIDED_VOLUME_BOUNDS.min} to ${GUIDED_VOLUME_BOUNDS.max} GB.`
  }
  return out
}

/** Why a body-size limit cannot be deployed, or null. */
export function maxBodyError(value: number | undefined): string | null {
  if (value === undefined) return null
  return Number.isInteger(value) && value >= 1 && value <= GUIDED_MAX_BODY_MIB ? null : `Enter a size from 1 to ${GUIDED_MAX_BODY_MIB} MiB.`
}

/** Where Configure keeps a wallet's guided draft. */
export function draftStorageKey(wallet: string | undefined): string {
  return `ddws.console.draft.${wallet ?? 'anonymous'}`
}

/** The stored guided draft, normalized, or `null` when none is readable. */
export function readStoredDraft(wallet: string | undefined): GuidedDraft | null {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(draftStorageKey(wallet)) ?? 'null') as Partial<GuidedDraft> | null
    if (!parsed || typeof parsed !== 'object') return null
    return normalizeGuidedDraft(parsed)
  } catch {
    return null
  }
}

/**
 * Whether a draft still holds only what Configure starts with. The price cap,
 * recovery and replica settings are left out: none of them is something a
 * tenant would call "my unfinished machine". Whatever size a host's Size card
 * opened on is something Configure chose, so it counts as untouched too.
 */
export function isUntouchedDraft(draft: GuidedDraft): boolean {
  const base = DEFAULT_GUIDED_DRAFT
  const startingSize = matchesPreset(draft, base) || draft.sizeAutoChosen === true
  return draft.name === base.name
    && draft.image === base.image
    && startingSize
    && JSON.stringify(draft.ports) === JSON.stringify(base.ports)
    && JSON.stringify(draft.env) === JSON.stringify(base.env)
    && draft.volume === undefined
}

/**
 * The lowest port at or above `start` that no TCP row uses yet, so "Add port"
 * never proposes a duplicate. Wraps to 1024 past 65535; returns `start` if every
 * port is taken.
 */
export function nextFreePort(ports: GuidedPort[], start = 8080): number {
  const used = new Set(ports.filter((port) => port.protocol === 'tcp').map((port) => port.container))
  for (let candidate = start; candidate <= 65535; candidate += 1) if (!used.has(candidate)) return candidate
  for (let candidate = 1024; candidate < start; candidate += 1) if (!used.has(candidate)) return candidate
  return start
}

/** Drafts saved before ports lost their public number and toggle keep only what still applies. */
function normalizePort(port: Partial<GuidedPort>): GuidedPort {
  const normalized: GuidedPort = { container: Number(port.container) || 0, protocol: port.protocol === 'udp' ? 'udp' : 'tcp' }
  // Omitted rather than set to undefined, so a port without it keeps the shape stored drafts compare by.
  if (typeof port.maxBodyMiB === 'number' && Number.isFinite(port.maxBodyMiB)) normalized.maxBodyMiB = port.maxBodyMiB
  if (Array.isArray(port.allowFrom)) normalized.allowFrom = port.allowFrom.filter((entry): entry is string => typeof entry === 'string')
  return normalized
}

/** Ports set to "Only these IPs". */
export function restrictedPorts(ports: readonly GuidedPort[]): GuidedPort[] {
  return ports.filter((port) => port.allowFrom !== undefined)
}

/** Whether renting this draft needs a host that enforces allowlists. */
export function requiresAllowlist(draft: Pick<GuidedDraft, 'ports'>): boolean {
  return draft.ports.some((port) => (port.allowFrom?.length ?? 0) > 0)
}

/** Whether a port's body-size limit reaches the SDL (see `GuidedPort.maxBodyMiB`). */
export function servesHttpIngress(port: GuidedPort): boolean {
  return port.protocol === 'tcp' && port.container === INGRESS_PORT
}

function decimalCpu(millicores: number): string {
  return (millicores / 1000).toFixed(3).replace(/0+$/, '').replace(/\.$/, '')
}

/** Convert a human dollar value into the integer micro-dollar amount expected by SDL. */
export function aktToUakt(value: string): string {
  const raw = value.trim()
  if (!/^\d+(?:\.\d+)?$/.test(raw)) throw new Error('Price cap must be a positive dollar amount.')
  const [whole = '0', fraction = ''] = raw.split('.')
  const micros = `${whole}${fraction.padEnd(6, '0').slice(0, 6)}`.replace(/^0+(?=\d)/, '')
  if (micros === '0') throw new Error('Price cap must be greater than zero.')
  return micros
}

export interface PriceCapSummary {
  hourly: Coin
  daily: Coin
  monthly: Coin
}

/** Convert the editable hourly dollar ceiling into exact period ceilings. */
export function priceCapSummary(value: string): PriceCapSummary {
  const hourly = BigInt(aktToUakt(value))
  return {
    hourly: { denom: 'uddt', amount: hourly.toString() },
    daily: { denom: 'uddt', amount: (hourly * 24n).toString() },
    monthly: { denom: 'uddt', amount: (hourly * 720n).toString() },
  }
}

/** Convert the plan's per-hour cap into SDL's integer micro-dollar-per-block cap. */
export function hourlyAktToUaktPerBlock(value: string, blockSeconds = 6): string {
  if (!Number.isInteger(blockSeconds) || blockSeconds <= 0 || 3600 % blockSeconds !== 0) {
    throw new Error('Block duration must divide one hour.')
  }
  const hourlyMicros = BigInt(aktToUakt(value))
  const divisor = BigInt(3600 / blockSeconds)
  const perBlock = (hourlyMicros + divisor - 1n) / divisor
  return perBlock.toString()
}

/** How many copies of the service the SDL asks for: the `count` every unit price is multiplied by. */
export function replicaCount(draft: Pick<GuidedDraft, 'recoveryMode' | 'replicaCount'>): number {
  return draft.recoveryMode === 'replicated' ? Math.max(2, Math.min(10, Math.round(draft.replicaCount))) : 1
}

/**
 * The open request's "Highest price you'll accept", in dollars per hour for
 * the whole instance, as the SDL's per-unit, per-block ceiling.
 *
 * The chain's ceiling for an order is the group's price, `amount × count`
 * (`GroupSpec.Price` → `ResourceUnit.FullPrice`), and an offer prices the whole
 * group too. So the typed total is divided by `count`: with three replicas,
 * $0.90/hour must sign a third of it per unit, or the chain would accept
 * offers up to $2.70/hour while the page said $0.90.
 *
 * Rounded DOWN, unlike `hourlyAktToUaktPerBlock`: this is a limit the tenant
 * typed, and rounding it up would let an offer above the shown price be
 * accepted. Throws on an amount that is not a dollar figure, and on one so
 * small that no whole `uddt` per block fits under it.
 */
export function highestPricePerBlock(hourly: string, blockSeconds = 6, count = 1): string {
  if (!Number.isInteger(blockSeconds) || blockSeconds <= 0 || 3600 % blockSeconds !== 0) {
    throw new Error('Block duration must divide one hour.')
  }
  const raw = hourly.trim()
  if (!/^\d+(?:\.\d+)?$/.test(raw)) throw new Error('Enter a price per hour in dollars, such as 0.90.')
  // Digits past the sixth decimal are dropped, which also rounds down.
  const [whole = '0', fraction = ''] = raw.split('.')
  const micros = BigInt(`${whole}${fraction.padEnd(6, '0').slice(0, 6)}`)
  if (!Number.isInteger(count) || count < 1) throw new Error('Replica count must be a positive whole number.')
  // Both divisions floor, so the group ceiling (perBlock × count) never exceeds what was typed.
  const perBlock = micros / BigInt(3600 / blockSeconds) / BigInt(count)
  if (perBlock <= 0n) throw new Error(`That price is below the smallest price the chain can express. Enter at least $${(0.0006 * count).toFixed(4)} per hour.`)
  // The ceiling an empty field sends is per unit, so the most a request can
  // carry is that × count. Checked on the typed total, which is what the
  // message names: "at most $6000" beside an accepted $7000 read as a bug.
  const most = BigInt(OPEN_REQUEST_HOURLY_CEILING) * BigInt(count)
  if (micros > most * 1_000_000n) throw new Error(`Enter at most $${most.toString()} per hour${count > 1 ? ` for all ${count} replicas` : ''}, the most a request can carry. Leave it empty for no limit.`)
  return perBlock.toString()
}

/** Build the SDL consumed by the existing, Go-verified conversion pipeline. */
export function guidedDraftToSdl(draft: GuidedDraft, perBlockPrice?: string): string {
  const buildError = buildErrors(draft)
  if (buildError) throw new Error(buildError)
  const ports = draft.ports.map((port) => {
    const lines = [`      - port: ${port.container}`, `        as: ${port.container}`]
    if (port.protocol !== 'tcp') lines.push(`        proto: ${port.protocol}`)
    if (port.maxBodyMiB !== undefined && servesHttpIngress(port)) {
      lines.push('        http_options:', `          max_body_size: ${Math.round(port.maxBodyMiB) * MIB}`)
    }
    lines.push('        to:', '          - global: true')
    if (port.allowFrom !== undefined) {
      // "Only these IPs" with nothing listed must never become an open port.
      const list = normalizeAllowFrom(port.allowFrom)
      if (!list.ok) throw new Error(`Port ${port.container}: ${list.message}`)
      if (list.value.length === 0) throw new Error(`Port ${port.container} allows only listed addresses, and none are listed.`)
      lines.push('            allow_from:', ...list.value.map((entry) => `              - ${JSON.stringify(entry)}`))
    }
    return lines.join('\n')
  }).join('\n')
  // Only a host that enforces the list may take the lease (brief, trap 3).
  const attributes = [...(requiresAllowlist(draft) ? [ALLOWLIST_CAPABILITY] : []), ...(isBuildImage(draft.image) ? [BUILD_CAPABILITY] : [])]
  const placementAttributes = attributes.length ? `\n      attributes:${attributes.map((key) => `\n        ${key}: "true"`).join('')}` : ''
  // Each entry is a double-quoted scalar. Bare, `A=foo: bar` parses as a
  // mapping and `B=x #c` loses its tail to a comment. JSON string syntax is
  // valid YAML double-quoted syntax, so JSON.stringify escapes it exactly.
  const env = draft.env.filter((row) => row.key.trim()).map((row) => `      - ${JSON.stringify(`${row.key.trim()}=${row.value}`)}`).join('\n')
  const envBlock = env ? `\n    env:\n${env}` : ''
  // A volume is a second, named storage entry marked persistent and mounted
  // through `params`. `class` is left to the SDL parser, which sets it to
  // `default` for a persistent volume without one: the class every provider
  // advertises. The ephemeral root disk stays the first, unnamed entry.
  const volume = draft.volume
  const paramsBlock = volume
    ? `\n    params:\n      storage:\n        ${GUIDED_VOLUME_NAME}:\n          mount: ${JSON.stringify(volume.mount.trim())}`
    : ''
  const rootStorage = `${Math.max(1, Math.round(draft.storage))}Gi`
  const storage = volume
    ? `\n          - size: ${rootStorage}\n          - name: ${GUIDED_VOLUME_NAME}\n            size: ${Math.max(1, Math.round(volume.sizeGi))}Gi\n            attributes:\n              persistent: true`
    : `\n          size: ${rootStorage}`
  const cpu = decimalCpu(Math.max(1, Math.round(draft.cpu)))
  const ram = `${Math.max(1, Math.round(draft.ram))}Gi`
  // A blank cap is the competitive flow's explicit "open request" marker.
  // Direct rentals call directRentalDraftToSdl first, which supplies the
  // provider's signed posting and therefore never reaches this fallback.
  const price = perBlockPrice ?? hourlyAktToUaktPerBlock(draft.priceCap.trim() || OPEN_REQUEST_HOURLY_CEILING)
  return `version: "2.0"

services:
  app:
    image: ${draft.image.trim()}
    expose:
${ports}
${envBlock}${paramsBlock}

profiles:
  compute:
    app:
      resources:
        cpu:
          units: ${cpu}
        memory:
          size: ${ram}
        storage:${storage}
  placement:
    dcloud:${placementAttributes}
      pricing:
        app:
          denom: uddt
          amount: ${price}

deployment:
  app:
    dcloud:
      profile: app
      count: ${replicaCount(draft)}
`
}

/**
 * Build SDL for a competitive request. With no `highest` (dollars per hour,
 * for the whole instance) it carries the chain ceiling, exactly as before the
 * field existed; with one it carries that price per unit and block, rounded
 * down (`highestPricePerBlock`).
 */
export function openRequestDraftToSdl(draft: GuidedDraft, highest?: string): string {
  const cap = highest?.trim()
  return guidedDraftToSdl({ ...draft, priceCap: '' }, cap ? highestPricePerBlock(cap, 6, replicaCount(draft)) : undefined)
}

/**
 * Build the SDL carried by a fixed-price rental request.
 *
 * The SDL schema still requires a positive placement amount even though a
 * direct rental is priced by the provider's signed quote. The guided form
 * deliberately leaves its price field empty on a direct path so that an
 * untyped default cannot turn the request back into a competitive one. A
 * validated provider posting is required to fill the SDL ceiling; no
 * technical fallback is safe because it could authorise a price the tenant
 * never saw.
 */
export function directRentalDraftToSdl(draft: GuidedDraft): string {
  const priceCap = draft.priceCap.trim()
  if (priceCap === '') {
    const providerPrice = draft.providerPostedPrice?.trim()
    if (!providerPrice) throw new Error('A provider-posted price is required for a direct rental.')
    return guidedDraftToSdl({ ...draft, priceCap: providerPrice })
  }
  return guidedDraftToSdl({ ...draft, priceCap })
}

/**
 * Serialize a stored draft for a provider-facing rental operation.
 *
 * A blank cap is the persisted marker for a direct rental when this serializer
 * is used by the provider-priced path; an open competitive request calls
 * `openRequestDraftToSdl` directly so it can use the chain ceiling. Keeping
 * the direct choice here means the quote, signed deployment, and later
 * manifest relay all derive the same SDL without writing the provider price
 * into the editable cap.
 */
export function rentalDraftToSdl(draft: GuidedDraft): string {
  return draft.priceCap.trim() === '' ? directRentalDraftToSdl(draft) : guidedDraftToSdl(draft)
}
import type { Coin } from './types'
import { HTTP_OPTION_LIMITS } from './sdl'
